// Name parsing helpers used by the pricing page + dashboard header.
//
// These exist because we kept rendering users' emails in greeting slots:
//   - When `leadName` was empty, the old code fell back to the local part of
//     the email (e.g. "chris" from "chris@jobtest.org") — at best ugly,
//     and `+`-suffixed or no-separator local parts (e.g. "chris+test") looked
//     like email scraps in a greeting.
//   - More damaging: when a user typed their EMAIL into the name field on
//     /lead, the name regex `split(/\s+/)[0]` would return the whole email
//     unchanged — so the pricing page literally rendered "Hey
//     chris@example.com,".
//
// `firstNameOrNull` rejects anything containing `@` so that email-shaped
// inputs (whether from a mis-filled form or a stale lead row) never surface
// as a personalized greeting. Callers decide what to show when it returns
// null (typically a generic "there" or "Your report is ready.").

/**
 * Pull a safe first name out of a raw lead/user name string.
 * Returns `null` for missing, empty, or email-shaped input.
 */
export function firstNameOrNull(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Defense: never render an `@`-containing string as a "first name".
  if (trimmed.includes('@')) return null;
  const first = trimmed.split(/\s+/)[0];
  return first || null;
}

/**
 * 1–2 character initials for an avatar pill. Returns '?' when nothing
 * usable is available (rather than '' so the pill never collapses). Never
 * derives anything from an email address — if name is missing, returns '?'.
 */
export function safeInitials(name: string | null | undefined): string {
  const first = firstNameOrNull(name);
  if (!first) return '?';
  // Use the full trimmed name (could be "First Last") for two-letter initials.
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase() || '?';
  }
  return (first[0] ?? '?').toUpperCase();
}

/**
 * Display label for the header / nav menu. Falls back to a neutral phrase
 * rather than the email so we never expose the email in chrome that's
 * visible on shared screens.
 */
export function safeDisplayName(name: string | null | undefined): string {
  return firstNameOrNull(name) ?? 'Your account';
}
